import React, { useEffect, useRef, useState } from "react";
import { ContentContainer } from "../../../components/layout";
import { useLazyQuery, useQuery } from "@apollo/client";
import { POD_SUMMARY_DATA_QUERY } from "../../../constants/graphql/POD-queries";
import { LOCKER_INFO } from "../../../constants/graphql/locker-queries";
import {
  formatDate,
  formatDateTime,
  formatDateYYYYMMDD,
  getSixMonthsAgo,
  toUTCDateTimeString,
} from "../../../algorithms/date";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import {
  Button,
  Checkbox,
  DownloadButton,
  LoadingComponent,
  Modal,
  MultiSelect,
  NoDataFoundTemplate,
  Select,
} from "../../../components/core";
import { Card } from "primereact/card";
import PrimereactDateRange from "../../../components/core/date-range-primereact";
import {
  QUERY_LOCKER_NAME,
  QUERY_LOCKER_TYPE,
} from "../../../constants/graphql/drop-off-graphql";
import { Strings } from "../../../constants/strings";
import { NEWEST_LOCKER_NAME_QUERY } from "../../../features/stay-time-in-locker/stay-time-query/stay-time-graphql";

const PODSummaryReport = () => {
  const operatorId = JSON.parse(localStorage.getItem("user")).operator_id;
  const dt = useRef(null);
  const maxStartDate = getSixMonthsAgo();

  // local variables
  const [nameData, setNameData] = useState([]);
  const [allLockersSelected, setAllLockersSelected] = useState(false);
  const [selectedLockerType, setSelectedLockerType] = useState("-1");
  const [lockerName, setLockerName] = useState([]);
  const [lockerType, setLockerType] = useState([]);
  const [selectedLockerName, setSelectedLockerName] = useState([]);
  const [searchResultHeader, setSearchResultHeader] = useState("");

  // table variables
  const [filterButtonClicked, setFilterButtonClicked] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const [podSummaryTable, setPodSummaryTable] = useState([]);
  const [totalAmt, setTotalAmt] = useState(0);
  const [totalPackages, setTotalPackages] = useState(0);
  const [totalFees, setTotalFees] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [filterVariables, setFilterVariables] = useState({
    startTime: "00:00",
    endTime: "23:59",
    startDate: (() => {
      const d = new Date();
      d.setMonth(d.getMonth() - 6);
      return d;
    })(),
    endDate: new Date(),
  });

  // gql
  const [fetchSummaryPOD, { data: podSummaryLazy, loading }] = useLazyQuery(
    POD_SUMMARY_DATA_QUERY
  );
  const { data: allLockerNames } = useQuery(LOCKER_INFO, {
    variables: {
      operatorId: operatorId,
    },
  });
  const [fetchLockerName, { data: lockerNameData }] = useLazyQuery(
    NEWEST_LOCKER_NAME_QUERY
  );
  const fetchLockerType = useQuery(QUERY_LOCKER_TYPE);

  const findLockerName = (lockerId, nameData) => {
    const locker = nameData.find(
      (locker) => parseInt(locker.lockerId) === parseInt(lockerId)
    );

    return locker ? locker.lockerName : null;
  };

  // validation
  const [validation, setValidation] = useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  useEffect(() => {
    if (fetchLockerType.data) {
      let tempData = [];
      fetchLockerType.data.lockerTypes.forEach((element, index) =>
        tempData.push({
          value: element.lockerTypeId,
          name: element.lockerType,
        })
      );
      setLockerType(tempData);
    }
  }, [fetchLockerType.data]);

  useEffect(() => {
    if (allLockerNames && allLockerNames.lockerInfo) {
      const lockerDetails = allLockerNames.lockerInfo.map((locker) => ({
        lockerId: locker.lockerId,
        lockerName: locker.lockerName,
      }));
      setNameData(lockerDetails);
    }
  }, [allLockerNames]);
  useEffect(() => {
    if (selectedLockerName.length !== 0) {
      const hasZeroValue = selectedLockerName.some((item) => item.value === 0);
      if (hasZeroValue) {
        setAllLockersSelected(true);
      }
    }
  }, [selectedLockerName]);

  useEffect(() => {
    if (allLockersSelected) {
      const selectedLockers = lockerName.filter((locker) => locker.value !== 0);

      setSelectedLockerName(selectedLockers);
    } else {
      setSelectedLockerName([]);
    }
  }, [allLockersSelected, lockerName]);

  useEffect(() => {
    if (selectedLockerType !== "-1") {
      fetchLockerName({
        variables: {
          operatorId: operatorId,
          lockerTypeId: selectedLockerType,
        },
      });
    }
  }, [selectedLockerType]);

  useEffect(() => {
    if (lockerNameData) {
      const tempData = lockerNameData.lockerInfo.map((element) => ({
        label: element.lockerName,
        value: element.lockerId,
      }));

      if (!allLockersSelected) {
        tempData.unshift({
          value: 0,
          label: "All Lockers",
        });
      }

      setLockerName(tempData);
    }
  }, [lockerNameData, allLockersSelected]);

  useEffect(() => {
    if (loading) {
      setIsLoading(true);
    }
  }, [loading]);
  useEffect(() => {
    if (podSummaryLazy && podSummaryLazy.podSummary.length > 0) {
      setIsLoading(true);
      // Map the podSummary data without percentage calculation
      const data = podSummaryLazy.podSummary.map((pod) => {
        return {
          lockerId: pod.lockerId,
          lockerName: findLockerName(pod.lockerId, nameData),
          paymentCurrency: pod.paymentCurrency,
          earliestPackage: formatDateTime(pod.earliestPackage),
          latestPackage: formatDateTime(pod.latestPackage),
          totalPackages: pod.packages,
          totalPrice: pod.totalPrice,
          totalFees: pod.totalFees,
          totalPayments: pod.totalPayments,
          percentPerLockerPayments: pod.percentPerLockerPayments,
          percentPerLockerPrice: pod.percentPerLockerPrice,
          percentPerLockerPackages: pod.percentPerLockerPackages,
        };
      });

      setPodSummaryTable(data);

      const overallTotalPayments = data.reduce(
        (acc, item) => acc + item.totalPayments,
        0
      );
      setTotalAmt(overallTotalPayments.toFixed(2));

      const overallTotalPrice = data.reduce(
        (acc, item) => acc + item.totalPrice,
        0
      );
      setTotalPrice(overallTotalPrice.toFixed(2));

      const overallTotalfEES = data.reduce(
        (acc, item) => acc + item.totalFees,
        0
      );
      setTotalFees(overallTotalfEES.toFixed(2));

      const overallTotalPackages = data.reduce(
        (acc, item) => acc + item.totalPackages,
        0
      );
      setTotalPackages(overallTotalPackages);

      setIsLoading(false);
      setFilterButtonClicked(false);
    } else {
      setPodSummaryTable([]);
      setIsLoading(false);
    }
  }, [podSummaryLazy]);

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          footer="Totals:"
          colSpan={1}
          footerStyle={{
            textAlign: "right",
            border: "2px solid rgb(168 162 158)",
          }}
          className="bg-primary-primaryYellowLight"
        />
        <Column
          colSpan={2}
          footerStyle={{
            border: "2px solid rgb(168 162 158)",
          }}
          footer={`${totalPackages} packages`}
          className="text-center bg-primary-primaryYellowLight"
        />

        {showMore && (
          <Column
            className="text-center bg-primary-primaryYellowLight"
            footerStyle={{
              border: "2px solid rgb(168 162 158)",
            }}
          />
        )}
        {showMore && (
          <Column
            className="text-center bg-primary-primaryYellowLight"
            footerStyle={{
              border: "2px solid rgb(168 162 158)",
            }}
          />
        )}
        <Column
          footer={`₱${totalPrice}`}
          colSpan={2}
          footerStyle={{
            border: "2px solid rgb(168 162 158)",
          }}
          className="text-center bg-primary-primaryYellowLight"
        />
        <Column
          footer={`₱${totalFees}`}
          colSpan={1}
          footerStyle={{
            border: "2px solid rgb(168 162 158)",
          }}
          className="text-center bg-primary-primaryYellowLight"
        />
        <Column
          footer={`₱${totalAmt}`}
          colSpan={2}
          footerStyle={{
            border: "2px solid rgb(168 162 158)",
          }}
          className="text-center bg-primary-primaryYellowLight"
        />
        {/* <Column footer={totalAmt} /> */}
      </Row>
    </ColumnGroup>
  );

  const showError = (title, message) => {
    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      title: title,
      message: message,
      open: true,
      actions: (
        <div className="w-full">
          <Button
            onClick={() => setValidation({ open: false })}
            value={"OK"}
            className={"p-2 w-full"}
          />
        </div>
      ),
    });

    setIsLoading(false);
  };
  const handleSearch = () => {
    // setIsLoading(true);
    // setPodSummaryTable([]);
    handleSearchResultHeader();

    if (filterVariables.startDate > filterVariables.endDate) {
      showError("Invalid Date", "Start date must be before end date.");
      setFilterButtonClicked(false);
      setIsClicked(false);
      return;
    }

    //date range picker validation
    if (filterVariables.startDate == "" && filterVariables.endDate == "") {
      showError("Invalid Date", "No date range selected");
      setFilterButtonClicked(false);

      setIsLoading(false);
      setIsClicked(false);

      return;
    } else if (filterVariables.endDate == "") {
      showError("Invalid Date", "No end date was selected");
      setFilterButtonClicked(false);

      setIsLoading(false);
      setIsClicked(false);

      return;
    } else if (filterVariables.startDate == "") {
      showError("Invalid Date", "No start date was selected");
      setFilterButtonClicked(false);

      setIsLoading(false);
      setIsClicked(false);

      return;
    }

    // uncomment pag may lockerid na
    if (selectedLockerType !== "-1" && selectedLockerName.length === 0) {
      showError(
        `${Strings.popup_error_title_search_criteria}`,
        `${Strings.popup_error_lockername}`
      );
      setFilterButtonClicked(false);

      setIsLoading(false);
      setIsClicked(false);

      return;
    } else {
      setIsClicked(true);
      setFilterButtonClicked(true);
    }
  };

  useEffect(() => {
    if (filterButtonClicked) {
      const lockerIdValues = selectedLockerName.map((locker) => locker.value);

      fetchSummaryPOD({
        variables: {
          operatorId: localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user")).operator_id
            : "",
          transactionDateStart: toUTCDateTimeString(
            new Date(filterVariables.startDate),
            filterVariables.startTime || "00:00"
          ),
          transactionDateEnd: toUTCDateTimeString(
            new Date(filterVariables.endDate),
            filterVariables.endTime || "23:59"
          ),
          lockerIds: lockerIdValues,
        },
      });

      setFilterButtonClicked(false);
    }
  }, [filterButtonClicked]);

  const toggleTableVisiblity = () => {
    setShowMore(!showMore);
  };

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          field="lockerName"
          header="Locker Name"
          rowSpan={2}
          sortable
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#FECD08",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />

        {/* <Column
					header="%"
					field="percentPerLocker"
					rowSpan={2}
					headerStyle={{ backgroundColor: "#FECD08" }}
				/> */}
        <Column
          field=""
          header="Total Packages"
          colSpan={2}
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#FECD08",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />
        {showMore && (
          <Column
            header={`Oldest Record`}
            field="earliestPackage"
            rowSpan={2}
            alignHeader={"center"}
            className="text-center"
            headerStyle={{
              backgroundColor: "#FECD08",
              color: "black",
              border: "2px solid rgb(168 162 158)",
            }}
          />
        )}

        {showMore && (
          <Column
            header={`Newest Record`}
            field="latestPackage"
            rowSpan={2}
            alignHeader={"center"}
            className="text-center"
            headerStyle={{
              backgroundColor: "#FECD08",
              color: "black",
              border: "2px solid rgb(168 162 158)",
            }}
          />
        )}
        <Column
          field=""
          header="Total Price"
          colSpan={2}
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#FECD08",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />

        <Column
          field="totalFees"
          header="Total Fees"
          colSpan={1}
          rowSpan={2}
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#FECD08",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />

        <Column
          field=""
          header="Total Payments"
          colSpan={2}
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#FECD08",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />
      </Row>
      <Row>
        <Column
          field="totalPackages"
          header="Total Packages"
          sortable
          colSpan={1}
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#facc15",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />
        <Column
          field="totalPackages"
          header="%"
          sortable
          colSpan={1}
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#facc15",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />
        <Column
          field="totalPrice"
          header="Total Price"
          colSpan={1}
          sortable
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#facc15",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />
        <Column
          field="totalPrice"
          header="%"
          sortable
          colSpan={1}
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#facc15",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />
        <Column
          field="totalPayments"
          header="Total Payments"
          colSpan={1}
          sortable
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#facc15",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />
        <Column
          field="totalPayments"
          header="%"
          sortable
          colSpan={1}
          alignHeader={"center"}
          headerStyle={{
            backgroundColor: "#facc15",
            color: "black",
            border: "2px solid rgb(168 162 158)",
          }}
        />
      </Row>
    </ColumnGroup>
  );

  const handleSearchResultHeader = () => {
    let lockerNameText;
    if (selectedLockerName.length === lockerName.length) {
      lockerNameText = "ALL Lockers";
    } else {
      const lockerLabels = selectedLockerName
        .map((selectedLocker) => {
          const matchedLocker = lockerName.find(
            (item) => parseInt(item.value) === parseInt(selectedLocker.value)
          );
          return matchedLocker ? matchedLocker.label : null;
        })
        .filter((label) => label !== null);

      lockerNameText =
        lockerLabels.length > 0 ? lockerLabels.join(", ") : "ALL Lockers";
    }

    setSearchResultHeader(
      `${
        selectedLockerType !== "-1"
          ? lockerType.find((x) => x.value === selectedLockerType).name
          : "ALL Locker Types"
      },  ${lockerNameText}, from ${formatDate(
        filterVariables.startDate
      )} to ${formatDate(filterVariables.endDate)}`
    );
  };

  // handle change filter variables
  const handleFilterChange = (e, field) => {
    setIsClicked(false);
    let value = e.target.value;

    if (field === "startDate" || field === "endDate") {
      value = value ? new Date(value) : null;
    }

    setFilterVariables({
      ...filterVariables,
      [field]: value,
    });
  };

  return (
    <>
      <ContentContainer containerTitle={"Payment on Delivery Summary Report"}>
        <div className="px-4">
          <h2 className={"font-bold"}>Locker:</h2>
          <div className={"grid grid-cols-8 items-center w-full gap-y-2"}>
            <div className="col-span-6 grid">
              <div className="grid grid-cols-3 w-full items-center">
                <div className={"col-span-1"}>Locker Type:</div>
                <Select
                  defaultOption={{ value: "-1", name: "--- ALL ---" }}
                  items={lockerType}
                  className={"col-span-2"}
                  value={selectedLockerType}
                  onChange={(event) => {
                    setAllLockersSelected(false);
                    setSelectedLockerName([]);
                    setSelectedLockerType(event.target.value);
                  }}
                />
              </div>
            </div>
            <div className="col-span-2"></div>
            <div className="col-span-6 grid">
              <div className="grid grid-cols-3 w-full items-center">
                <div className={"col-span-1"}>Locker Name:</div>

                <MultiSelect
                  // className=""
                  placeholder={`${
                    selectedLockerType === "-1" ? "ALL Lockers" : "Select..."
                  }`}
                  items={lockerName}
                  disabled={selectedLockerType === "-1"}
                  // defaultOption={{ value: "-1", name: "-- ALL --" }}
                  value={selectedLockerName}
                  className={"col-span-2"}
                  onChange={(event) => {
                    setSelectedLockerName(event.target.value);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-5 pt-[8px]">
            <div className={"font-bold col-span-5"}>Payment Date filter:</div>

            <PrimereactDateRange
              className={`col-span-4`}
              startDate={filterVariables.startDate}
              endDate={filterVariables.endDate}
              startTime={filterVariables.startTime}
              endTime={filterVariables.endTime}
              startDateName="startDate"
              endDateName="endDate"
              startTimeName="startTime"
              endTimeName="endTime"
              onChange={(event, fieldName) => {
                handleFilterChange(event, fieldName);
              }}
              readOnlyInput={true}
              max={new Date()}
              min={maxStartDate}
              timeOnly={true}
            />
          </div>

          <div className="grid grid-cols-2 w-1/2 gap-y-2">
            <div></div>
            <u
              className="flex hover:text-blue-500 cursor-pointer mt-4 ml-auto w-min"
              onClick={() => {
                setFilterVariables({
                  ...filterVariables,
                  startDate: maxStartDate,
                  endDate: new Date(),
                  endTime: "23:59",
                  startTime: "00:00",
                });
                setSelectedLockerType("-1");
                setSelectedLockerName([]);
              }}
            >
              <i className="fas fa-undo my-auto"></i>
              <p className="ml-1">Reset</p>
            </u>
          </div>
        </div>
        <Button
          value={"Search"}
          disabled={isLoading}
          onClick={handleSearch}
          className={"w-40 h-8 m-5"}
        />

        {isClicked &&
          (isLoading ? (
            <LoadingComponent />
          ) : (
            <Card className={`${podSummaryTable.length === 0 ? "hidden" : ""}`}>
              <div>
                <Checkbox
                  checked={showMore}
                  onChange={toggleTableVisiblity}
                  label="Show More Details"
                />
              </div>
              <DownloadButton dt={dt} csvOnly={true} />

              <div className={" bg-primary-primaryYellow p-2 "}>
                <div className={"text-center"}>
                  <span>
                    <h1 className={"font-bold text-lg text-black"}>
                      POD SUMMARY REPORT
                    </h1>
                  </span>
                  <span>
                    <h1
                      className={"font-bold text-lg text-black"}
                    >{`For ${searchResultHeader} `}</h1>
                  </span>
                </div>
              </div>
              <DataTable
                value={podSummaryTable}
                footerColumnGroup={footerGroup}
                sortMode="multiple"
                stripedRows
                exportFilename="POD - Summary Report"
                tableStyle={{
                  minWidth: "50rem",
                  borderWidth: 2,
                  borderColor: "#E6E6E6",
                }}
                ref={dt}
                removableSort
                headerColumnGroup={headerGroup}
              >
                <Column
                  header={`Locker Name`}
                  field="lockerName"
                  sortable
                  alignHeader={"center"}
                  className="font-semibold border-x-2 border-b-0 border-x-[#a8a29e]"
                  // bodyClassName={{
                  // 	bor: "2px solid #a8a29e",
                  // }}
                  headerStyle={{ backgroundColor: "#FECD08" }}
                />
                {/* <Column
									header="%"
									field="percentPerLocker"
									sortable
									alignHeader="center"
									className="text-center"
									headerStyle={{ backgroundColor: "#FECD08" }}
									body={(rowData) => {
										// Remove the '%' sign and convert to a number for proper comparison
										const percentValue = parseFloat(
											rowData.percentPerLocker.replace("%", "")
										);

										return (
											<div
												className={`${
													percentValue > 50.0
														? "text-green-600"
														: "text-orange-400"
												}`}
											>
												{rowData.percentPerLocker}
											</div>
										);
									}}
								/> */}

                <Column
                  header={`Total Packages`}
                  field="totalPackages"
                  sortable
                  alignHeader={"center"}
                  className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                  headerStyle={{ backgroundColor: "#FECD08" }}
                />

                <Column
                  header={`%s`}
                  field="percentPerLockerPackages"
                  alignHeader={"center"}
                  className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                  headerStyle={{ backgroundColor: "#FECD08" }}
                  body={(rowData) => {
                    return (
                      <div
                        className={`${
                          rowData.percentPerLockerPackages > 50.0
                            ? "text-green-600"
                            : "text-orange-400"
                        }`}
                      >
                        {rowData.percentPerLockerPackages}%
                      </div>
                    );
                  }}
                />
                {showMore && (
                  <Column
                    header={`Oldest Record`}
                    field="earliestPackage"
                    sortable
                    alignHeader={"center"}
                    className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                    headerStyle={{ backgroundColor: "#FECD08" }}
                  />
                )}

                {showMore && (
                  <Column
                    header={`Newest Record`}
                    field="latestPackage"
                    sortable
                    alignHeader={"center"}
                    className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                    headerStyle={{ backgroundColor: "#FECD08" }}
                  />
                )}
                <Column
                  header={`Total Price`}
                  field="totalPrice"
                  alignHeader={"center"}
                  className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                  headerStyle={{ backgroundColor: "#FECD08" }}
                  body={(rowData) => <div>₱ {rowData.totalPrice}</div>}
                />
                <Column
                  header={`%`}
                  field="percentPerLockerPrice"
                  alignHeader={"center"}
                  className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                  headerStyle={{ backgroundColor: "#FECD08" }}
                  body={(rowData) => {
                    return (
                      <div
                        className={`${
                          rowData.percentPerLockerPrice > 50.0
                            ? "text-green-600"
                            : "text-orange-400"
                        }`}
                      >
                        {rowData.percentPerLockerPrice}%
                      </div>
                    );
                  }}
                />
                <Column
                  header={`Total Fees`}
                  field="totalFees"
                  alignHeader={"center"}
                  className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                  headerStyle={{ backgroundColor: "#FECD08" }}
                  body={(rowData) => <div>₱ {rowData.totalFees}</div>}
                />
                <Column
                  header={`Total Payments`}
                  field="totalPayments"
                  alignHeader={"center"}
                  className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                  headerStyle={{ backgroundColor: "#FECD08" }}
                  body={(rowData) => <div>₱ {rowData.totalPayments}</div>}
                />
                <Column
                  header={`%`}
                  field="percentPerLockerPayments"
                  alignHeader={"center"}
                  className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                  headerStyle={{ backgroundColor: "#FECD08" }}
                  body={(rowData) => {
                    return (
                      <div
                        className={`${
                          rowData.percentPerLockerPayments > 50.0
                            ? "text-green-600"
                            : "text-orange-400"
                        }`}
                      >
                        {rowData.percentPerLockerPayments}%
                      </div>
                    );
                  }}
                />
                {/* <footer></footer> */}
              </DataTable>
            </Card>
          ))}

        {!isLoading && isClicked && podSummaryTable.length === 0 && (
          <NoDataFoundTemplate />
        )}

        <Modal
          icon={validation.icon}
          title={validation.title}
          message={validation.message}
          open={validation.open}
          actions={validation.actions}
        />
      </ContentContainer>
    </>
  );
};

export default PODSummaryReport;
